import { useMediaQuery } from '@material-ui/core';
import { CustomTheme } from '@nemuru-eng/js-api-client';
import { generatePath } from 'react-router-dom';

import { channelAgentNameFieldColumn } from '@/components/modules/_shared/utils/DataGridFields/channelAgentNameFieldColumn';
import { documentNumberFieldColumn } from '@/components/modules/_shared/utils/DataGridFields/documentNumberFieldColumn';
import { lenderLogoFieldColumn } from '@/components/modules/_shared/utils/DataGridFields/lenderLogoFieldColumn';
import { statusFieldColumn } from '@/components/modules/_shared/utils/DataGridFields/statusFieldColumn';
import { toDealDetailFieldColumn } from '@/components/modules/_shared/utils/DataGridFields/toDealDetailFieldColumn';
import { updatedAtFieldColumn } from '@/components/modules/_shared/utils/DataGridFields/updatedAtFieldColumn';
import { LoanListSupportedApplicationType } from '@/components/modules/LoanListByProduct/types/LoanListSupportedApplicationType';
import { assetTypeFieldColumn } from '@/components/modules/LoanListByProduct/utils/LoanDataGridFields/components/assetTypeFieldColumn';
import { borrowerNameFieldColumn } from '@/components/modules/LoanListByProduct/utils/LoanDataGridFields/components/borrowerNameFieldColumn';
import { principalFieldColumn } from '@/components/modules/LoanListByProduct/utils/LoanDataGridFields/components/principalFieldColumn';
import { programFieldColumn } from '@/components/modules/LoanListByProduct/utils/LoanDataGridFields/components/programFieldColumn';
import { statusWithPrincipalAmountMobile } from '@/components/modules/LoanListByProduct/utils/LoanDataGridFields/components/statusWithPrincipalAmountMobile';
import { updatedAtWithBorrowerNameFieldColumnMobile } from '@/components/modules/LoanListByProduct/utils/LoanDataGridFields/components/updatedAtWithBorrowerNameFieldColumnMobile';
import { DETAILS_PATH_BY_PRODUCT } from '@/config/config';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface LoanDataGridFieldsProps {
  applicationType: LoanListSupportedApplicationType;
  reducedWidth?: boolean;
}

export function LoanDataGridFields({ applicationType, reducedWidth = false }: LoanDataGridFieldsProps) {
  const { breakpoints, themeName } = useCustomTheme();

  const showEntityColumn = themeName !== CustomTheme.CAIXABANK;

  const isDownMd = useMediaQuery(breakpoints.down('md'));
  const targetPath = DETAILS_PATH_BY_PRODUCT[applicationType];

  const linkTo = (id) => generatePath(targetPath, { loanId: id });
  const defaultConsumerLoanFields = {
    desktop: [
      assetTypeFieldColumn({ flex: 0.06 }),
      borrowerNameFieldColumn({ flex: 0.14 }),
      ...(reducedWidth
        ? []
        : [
            channelAgentNameFieldColumn({ flex: 0.16 }),
            showEntityColumn ? lenderLogoFieldColumn({ flex: 0.1 }) : documentNumberFieldColumn({ flex: 0.1 }),
          ]),
      principalFieldColumn({ flex: 0.11 }),
      updatedAtFieldColumn({ flex: 0.12 }),
      statusFieldColumn({ applicationType, flex: 0.21 }),
      toDealDetailFieldColumn({ linkTo, flex: 0.06 }),
    ],
    mobile: [
      updatedAtWithBorrowerNameFieldColumnMobile({ flex: 0.55 }),
      statusWithPrincipalAmountMobile({ flex: 0.45 }),
    ],
  };

  const creditLineFields = {
    desktop: [
      assetTypeFieldColumn({ flex: 0.06 }),
      borrowerNameFieldColumn({ flex: 0.13 }),
      ...(reducedWidth
        ? []
        : [
            channelAgentNameFieldColumn({ flex: 0.15 }),
            showEntityColumn ? lenderLogoFieldColumn({ flex: 0.1 }) : documentNumberFieldColumn({ flex: 0.1 }),
          ]),
      programFieldColumn({ flex: 0.11 }),
      principalFieldColumn({ flex: 0.09 }),
      updatedAtFieldColumn({ flex: 0.12 }),
      statusFieldColumn({ applicationType, flex: 0.18 }),
      toDealDetailFieldColumn({ linkTo, flex: 0.06 }),
    ],
    mobile: [
      updatedAtWithBorrowerNameFieldColumnMobile({ flex: 0.55 }),
      statusWithPrincipalAmountMobile({ flex: 0.45 }),
    ],
  };

  const cardFields = {
    desktop: [
      assetTypeFieldColumn({ flex: 0.06 }),
      borrowerNameFieldColumn({ flex: 0.13 }),
      ...(reducedWidth
        ? []
        : [
            channelAgentNameFieldColumn({ flex: 0.15 }),
            showEntityColumn ? lenderLogoFieldColumn({ flex: 0.1 }) : documentNumberFieldColumn({ flex: 0.1 }),
          ]),
      updatedAtFieldColumn({ flex: 0.12 }),
      statusFieldColumn({ applicationType, flex: 0.22 }),
      toDealDetailFieldColumn({ linkTo, flex: 0.06 }),
    ],
    mobile: [
      updatedAtWithBorrowerNameFieldColumnMobile({ flex: 0.55 }),
      statusWithPrincipalAmountMobile({ flex: 0.45 }),
    ],
  };

  const DataGridFields = {
    consumer_loan: defaultConsumerLoanFields.desktop,
    credit_line: creditLineFields.desktop,
    card: cardFields.desktop,
  };

  const MobileDataGridFields = {
    consumer_loan: defaultConsumerLoanFields.mobile,
    credit_line: creditLineFields.mobile,
    card: cardFields.mobile,
  };

  return isDownMd ? MobileDataGridFields[applicationType] : DataGridFields[applicationType];
}
